import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'antd';
import type { FormInstance } from 'antd';
import { Checkbox, Select, Upload, Input, Button, RichArea, Popup } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { ExperienceList, FormListDoubleInputSelect, FormListDoubleSelect, FormListSingle } from '_molecules';
import { FormListSources } from '_molecules/FormListSources';
import { validatorL } from '_templates/CreateOpening/utils';
import { getCandidateData, getCandidateSourcesAPI, getDocuments, importCandidateByUrl, uploadPdfCv } from 'actions';
import {
  getCandidateCompanies,
  getCandidateCountries,
  getCandidateSpecialities,
  getCandidateTags,
} from 'store/selectors/candidateSelectors';
import { getLanguages, getLevels, getLocations, getPositions } from 'store/selectors/commonSelectors';
import { SELECT_TYPES } from '_atoms/Select/constants';
import type { SelectOption as AtomSelectOption } from '_atoms/Select/types';
import { REQUIRED_RULE } from '_organisms/InterviewFeedback/constants';
import { EMPTY_CANDIDATE_DATA } from '_templates/CandidateCreation/constants';
import { candidateLevels, linkRegexp, TIME_ZONE } from 'constants/constants';
import { ReactComponent as Loader } from 'dist/icons/loader.svg';
import { useAppDispatch } from 'store';
import type { CandidateData } from 'store/reducers/candidateReducer';
import {
  EMAIL_VALIDATION,
  LATIN_VALIDATION,
  PHONE_VALIDATOR,
  SOCIAL_NETWORKS_OPTIONS,
  VALIDATION_MAX_100,
} from './constants';
import { dataMapper, deepEqual, detectNetwork } from './utyls';
import './styls.scss';

interface CandidateCreationFormProps {
  form: FormInstance;
  onCheckDuplicates: () => void;
  isSSC?: boolean;
  setLoading: (loading: boolean) => void;
}

export const CandidateCreationForm: React.FC<CandidateCreationFormProps> = ({
  form,
  onCheckDuplicates,
  isSSC,
  setLoading,
}) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const [openLoaderInfoCandidate, setOpenLoaderInfoCandidate] = useState(false);
  const [openSpeciality, setOpenSpeciality] = useState('');
  const [openCity, setOpenCity] = useState(false);
  const [importUrl, setImportUrl] = useState('');

  const offices = useSelector(getLocations);
  const countries = useSelector(getCandidateCountries);
  const languages = useSelector(getLanguages);
  const languageLevels = useSelector(getLevels);
  const positions = useSelector(getPositions);
  const companies = useSelector(getCandidateCompanies);
  const specialties = useSelector(getCandidateSpecialities);
  const tags = useSelector(getCandidateTags);

  const officesOptions = useMemo(() => offices.map(el => ({ value: el.uuid, label: el.name })), [offices]);
  const countriesOptions = useMemo(
    () =>
      countries.map(el => ({
        states: el.states.map(s => ({
          value: s.name,
          label: s.name,
          cities: s.cities.map(c => ({ value: c, label: c })),
        })),
        value: el.name,
        label: el.name,
      })),
    [countries]
  );
  const languageLevelsOptions = useMemo(() => languageLevels.map(el => ({ value: el, label: el })), [languageLevels]);
  const languagesOptions = useMemo(() => languages.map(el => ({ value: el, label: el })), [languages]);
  const positionsOptions = useMemo(() => positions.map(el => ({ value: el, label: el })), [positions]);
  const companiesOptions = useMemo(() => companies.map(el => ({ value: el.uuid, label: el.name })), [companies]);
  const specialtiesOptions = useMemo(() => specialties.map(el => ({ value: el.name, label: el.name })), [specialties]);
  const tagsOptions = useMemo(() => tags.map(el => ({ value: el.uuid, label: el.name })), [tags]);

  const setCandidateData = (response: unknown) => {
    const resp = (isSSC ? (response as { candidate_profile: CandidateData }).candidate_profile : response) as
      | CandidateData
      | undefined;
    const data = dataMapper(resp);
    form.setFields(data);
    if (resp?.cvs.length) {
      dispatch(getDocuments(resp.cvs as unknown as string[])).then(cvs => {
        form.setFieldValue(
          'cvs',
          cvs?.map(d => ({ uuid: d.uuid, uid: d.uuid, name: d.filename }))
        );
      });
    }
  };

  const onDropPdfFiles = ({ file }: { file: File }) => {
    const formData = new FormData();
    formData.append('file', file);
    setOpenLoaderInfoCandidate(true);
    uploadPdfCv(formData as unknown as Record<string, unknown>)
      .then(setCandidateData)
      .finally(() => setOpenLoaderInfoCandidate(false));
  };

  const onImportUrl = () => {
    setImportUrl('');
    const link = form.getFieldValue('resume_link');
    setOpenLoaderInfoCandidate(true);
    importCandidateByUrl(link)
      .then(setCandidateData)
      .then(onCheckDuplicates)
      .finally(() => setOpenLoaderInfoCandidate(false));
  };

  const validImportUrl = () => {
    const patt = new RegExp(linkRegexp, 'i');
    const link = form.getFieldValue('resume_link');
    if (!patt.test(link)) {
      form.setFields([
        {
          name: 'resume_link',
          errors: ["The candidate hasn't been found. Please check the correctness of the link provided."],
        },
      ]);
    } else {
      form.setFields([{ name: 'resume_link', errors: [] }]);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { resume_link, ...values } = form.getFieldsValue();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { description_initial, ...initialValues } = EMPTY_CANDIDATE_DATA;
      if (deepEqual(values, initialValues)) {
        setImportUrl('');
        onImportUrl();
      } else {
        setImportUrl(link);
      }
    }
  };

  const onDetectLink = (value, key) => {
    const fields = form.getFieldsValue();
    const { social_networks } = fields;
    const network = detectNetwork(value);
    Object.assign(social_networks[key] || {}, { network });
    form.setFieldsValue({ social_networks });
  };

  const onCountryChange = c => {
    if (!c) {
      form.setFieldValue('state', undefined);
    } else if (c.states.length === 1 && c.states[0].value === 'Default state') {
      form.setFieldValue('state', c.states[0]);
    }
  };

  useEffect(() => {
    if (!openCity && !openSpeciality) (document.activeElement as HTMLElement)?.blur();
  }, [openCity, openSpeciality]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      getCandidateData(id, isSSC).then(resp => {
        setCandidateData(resp);
        setLoading(false);
      });
    }
    dispatch(getCandidateSourcesAPI());
  }, []);

  return (
    <>
      <div className="form-candidate-creation__parsing">
        <Typography.Title2 className="form-candidate-creation__title" weight={TYPOGRAPHY_WEIGHT.BOLD} id="parsing">
          {t('translation:candidate_creation_form.title_parsing')}
        </Typography.Title2>
        <Row gutter={[32, 0]} align="bottom">
          <Col span={12}>
            <Input
              label={t('translation:candidate_creation_form.label_import_from')}
              placeholder={t('translation:candidate_creation_form.ph_link')}
              itemProps={{ name: 'resume_link' }}
            />
          </Col>
          <Col span={12}>
            <Button onClick={validImportUrl}>{t('translation:candidate_creation_form.button_import')}</Button>
          </Col>
        </Row>
        <Row gutter={[32, 0]}>
          <Col span={12}>
            <Upload
              itemProps={{ name: '' }}
              className="parsing-pdf-block__upload"
              label={t('translation:candidate_creation_form.label_parse_pdf')}
              onChange={onDropPdfFiles}
              accept="application/pdf"
              fileList={[]}
              defaultFileList={[]}
            />
          </Col>
        </Row>
      </div>

      <Typography.Title2 className="form-candidate-creation__title" weight={TYPOGRAPHY_WEIGHT.BOLD} id="profile">
        {t('translation:candidate_creation_form.title_profile')}
      </Typography.Title2>
      <Row gutter={[32, 0]}>
        <Col span={24} lg={12} md={12} sm={24}>
          <Input
            label={t('translation:candidate_creation_form.label_first_name_latin')}
            placeholder={t('translation:candidate_creation_form.ph_first_name_latin')}
            maxLength={50}
            required
            itemProps={{
              name: 'first_name_latin',
              rules: [LATIN_VALIDATION],
            }}
            onBlur={onCheckDuplicates}
          />
        </Col>
        <Col span={24} lg={12} md={12} sm={24}>
          <Input
            label={t('translation:candidate_creation_form.label_last_name_latin')}
            placeholder={t('translation:candidate_creation_form.ph_last_name_latin')}
            maxLength={50}
            required
            itemProps={{
              name: 'last_name_latin',
              rules: [LATIN_VALIDATION],
            }}
            onBlur={onCheckDuplicates}
          />
        </Col>
      </Row>
      <Row gutter={[32, 0]}>
        <Col span={12} lg={12} md={12} sm={24}>
          <Input
            label={t('translation:candidate_creation_form.label_first_name_local')}
            placeholder={t('translation:candidate_creation_form.ph_first_name_local')}
            maxLength={50}
            itemProps={{ name: 'first_name_local' }}
            onBlur={onCheckDuplicates}
          />
        </Col>
        <Col span={12} lg={12} md={12} sm={24}>
          <Input
            label={t('translation:candidate_creation_form.label_last_name_local')}
            placeholder={t('translation:candidate_creation_form.ph_last_name_local')}
            maxLength={50}
            itemProps={{ name: 'last_name_local' }}
            onBlur={onCheckDuplicates}
          />
        </Col>
      </Row>
      <Row gutter={[32, 0]}>
        <Col span={24} lg={12} md={12} sm={24}>
          <Select
            labelInValue
            label={t('translation:candidate_creation_form.label_country')}
            placeholder={t('translation:candidate_creation_form.ph_country')}
            required
            options={countriesOptions}
            itemProps={{ name: 'country', rules: [REQUIRED_RULE] }}
            onChange={onCountryChange}
            onClear={() => {
              form.setFieldValue('state', undefined);
              form.setFieldValue('city', []);
            }}
            allowClear
          />
        </Col>
        <Col span={24} lg={12} md={12} sm={24}>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.state !== curr.state}>
            {({ getFieldValue }) => (
              <Select
                className="single-select-tags"
                open={openCity}
                onDropdownVisibleChange={setOpenCity}
                onChange={() => setOpenCity(false)}
                labelInValue
                label={t('translation:candidate_creation_form.label_city')}
                placeholder={t('translation:candidate_creation_form.ph_city')}
                options={
                  countriesOptions
                    .find(el => el.value === getFieldValue('country')?.value)
                    ?.states.find(el => el.value === getFieldValue('state')?.value)?.cities || []
                }
                itemProps={{ name: 'city' }}
                disabled={!getFieldValue('state')?.value}
                maxTagCount={1}
                mode={SELECT_TYPES.CREATABLE}
                allowClear
              />
            )}
          </Form.Item>
        </Col>
      </Row>
      <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country}>
        {({ getFieldValue }) => {
          const options = countriesOptions.find(el => el.value === getFieldValue('country')?.value)?.states || [];
          if (!options.length || (options.length === 1 && options[0].label === 'Default state')) return null;
          return (
            <Row gutter={[32, 0]}>
              <Col span={24} lg={12} md={12} sm={24}>
                <Select
                  labelInValue
                  label={t('translation:candidate_creation_form.label_state')}
                  placeholder={t('translation:candidate_creation_form.ph_state')}
                  options={options}
                  itemProps={{ name: 'state' }}
                  disabled={!getFieldValue('country')}
                  allowClear
                />
              </Col>
            </Row>
          );
        }}
      </Form.Item>
      <Row gutter={[32, 0]}>
        <Col span={24} lg={12} md={12} sm={24}>
          <Select
            labelInValue
            label={t('translation:candidate_creation_form.label_office_location')}
            placeholder={t('translation:candidate_creation_form.ph_office_location')}
            mode={SELECT_TYPES.MULTIPLE}
            options={officesOptions}
            itemProps={{ name: 'offices' }}
            allowClear
          />
        </Col>
        <Col span={24} lg={12} md={12} sm={24}>
          <Select
            labelInValue
            label={t('translation:candidate_creation_form.label_time_zone')}
            placeholder={t('translation:candidate_creation_form.ph_time_zone')}
            options={TIME_ZONE as unknown as AtomSelectOption[]}
            itemProps={{ name: 'timezone' }}
          />
        </Col>
      </Row>

      <div className="form-candidate-creation__languages">
        <FormListDoubleSelect
          listName="foreign_languages"
          buttonText={t('translation:candidate_creation_form.button_add_language')}
          firstSelectProps={{
            name: 'language',
            label: t('translation:candidate_creation_form.label_foreign_language'),
            options: languagesOptions,
            placeholder: t('translation:candidate_creation_form.ph_foreign_language'),
          }}
          secondSelectProps={{
            name: 'level',
            placeholder: t('translation:candidate_creation_form.ph_language_level'),
            options: languageLevelsOptions,
          }}
          firstSelectValidator={validatorL('level', t('translation:candidate_creation_form.text_select_language'))}
          secondSelectValidator={validatorL(
            'language',
            t('translation:candidate_creation_form.text_select_language_level')
          )}
        />
      </div>

      <FormListSources />

      <div className="form-candidate-creation__relocation">
        <Typography.Label className="form-candidate-creation__title" weight={TYPOGRAPHY_WEIGHT.BOLD}>
          {t('translation:candidate_creation_form.label_relocation')}
        </Typography.Label>
        <Checkbox
          label={t('translation:candidate_creation_form.label_ready_to_relocate')}
          itemProps={{ name: 'is_ready_to_relocate' }}
        />
        <Checkbox
          label={t('translation:candidate_creation_form.label_ready_for_business_trips')}
          itemProps={{ name: 'is_ready_to_business_trips' }}
        />
      </div>

      <Typography.Title2
        className="form-candidate-creation__title"
        weight={TYPOGRAPHY_WEIGHT.BOLD}
        id="contact_information"
      >
        {t('translation:candidate_creation_form.title_contact_information')}
      </Typography.Title2>
      <Row gutter={[32, 0]}>
        <Col span={24} lg={12} md={12} sm={24}>
          <FormListSingle
            listName="phone"
            label={t('translation:candidate_creation_form.label_phone_number')}
            placeholder={t('translation:candidate_creation_form.ph_phone_number')}
            buttonText={t('translation:candidate_creation_form.button_add_phone_number')}
            maxCount={3}
            onBlur={onCheckDuplicates}
            maxLength={16}
            rules={[PHONE_VALIDATOR]}
          />
        </Col>
        <Col span={24} lg={12} md={12} sm={24}>
          <FormListSingle
            listName="email"
            label={t('translation:candidate_creation_form.label_email_address')}
            placeholder={t('translation:candidate_creation_form.ph_email_address')}
            buttonText={t('translation:candidate_creation_form.button_add_email')}
            maxCount={3}
            rules={[EMAIL_VALIDATION]}
            onBlur={onCheckDuplicates}
          />
        </Col>
      </Row>
      <Row gutter={[32, 0]}>
        <Col span={24} lg={12} md={12} sm={24}>
          <Input
            label={t('translation:candidate_creation_form.label_telegram')}
            placeholder={t('translation:candidate_creation_form.ph_telegram')}
            itemProps={{ name: 'telegram' }}
            maxLength={32}
          />
        </Col>
      </Row>

      <FormListDoubleInputSelect
        listName="social_networks"
        label={t('translation:candidate_creation_form.label_links')}
        buttonText={t('translation:candidate_creation_form.button_add_link')}
        firstItemName="link"
        secondItemName="network"
        inputProps={{
          placeholder: t('translation:candidate_creation_form.ph_link'),
          maxLength: 1000,
          onChange: (e, f) => onDetectLink(e.target.value, f.name),
        }}
        selectProps={{
          placeholder: t('translation:candidate_creation_form.ph_link_type'),
          options: SOCIAL_NETWORKS_OPTIONS,
        }}
        maxCount={5}
      />

      <div className="form-candidate-creation__experience">
        <Typography.Title2 className="form-candidate-creation__title" weight={TYPOGRAPHY_WEIGHT.BOLD} id="experience">
          {t('translation:candidate_creation_form.title_experience')}
        </Typography.Title2>
        <FormListDoubleSelect
          listName="specialities"
          buttonText={t('translation:candidate_creation_form.button_add_specialty')}
          firstSelectValidator={validatorL(
            'level',
            t('translation:candidate_creation_form.text_select_specialty'),
            'experience'
          )}
          secondSelectValidator={validatorL(
            'speciality',
            t('translation:candidate_creation_form.text_select_specialty'),
            'experience'
          )}
          firstSelectProps={{
            name: 'speciality',
            label: t('translation:candidate_creation_form.label_specialty'),
            placeholder: t('translation:candidate_creation_form.ph_specialty'),
            required: true,
            options: specialtiesOptions,
            open: openSpeciality,
            onDropdownVisibleChange: (open, field) => {
              setOpenSpeciality(open ? field : '');
            },
            onChange: () => setOpenSpeciality(''),
            labelInValue: true,
            className: 'single-select-tags',
            maxTagCount: 1,
            mode: SELECT_TYPES.CREATABLE,
            itemProps: { rules: [REQUIRED_RULE, VALIDATION_MAX_100] },
          }}
          secondSelectProps={{
            name: 'level',
            label: t('translation:candidate_creation_form.label_experience_level'),
            options: candidateLevels,
            allowClear: false,
          }}
          maxCount={10}
        />
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <Row gutter={[32, 0]}>
              <Col span={24} lg={12} md={12} sm={24}>
                <Upload
                  accept="application/pdf, .jpg, .doc, .docx, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.slideshow, application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  label={t('translation:candidate_creation_form.label_documents')}
                  multiple
                  maxCount={5}
                  itemProps={{ name: 'cvs' }}
                />
              </Col>
              <Col span={24} lg={12} md={12} sm={24}>
                <Input
                  label={t('translation:candidate_creation_form.label_cv_links')}
                  placeholder={t('translation:candidate_creation_form.ph_link')}
                  itemProps={{ name: 'cv_link_0' }}
                />
                <Input
                  placeholder={t('translation:candidate_creation_form.ph_link')}
                  itemProps={{ name: 'cv_link_1' }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Select
          labelInValue
          label={t('translation:candidate_creation_form.label_tags')}
          placeholder={t('translation:candidate_creation_form.ph_tags')}
          mode={SELECT_TYPES.CREATABLE}
          options={tagsOptions}
          itemProps={{ name: 'tags', rules: [VALIDATION_MAX_100] }}
        />
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.description_initial !== curr.description_initial}>
          {({ getFieldValue, setFieldValue }) => (
            <Form.Item noStyle name="description">
              <RichArea
                label={t('translation:candidate_creation_form.label_description')}
                placeholder={t('translation:candidate_creation_form.ph_description')}
                className="form-candidate-creation__description"
                initialValue={getFieldValue('description_initial')}
                onChange={v => setFieldValue('description', v)}
              />
            </Form.Item>
          )}
        </Form.Item>
      </div>

      <Typography.Title2
        id="working_experience"
        className="form-candidate-creation__title"
        weight={TYPOGRAPHY_WEIGHT.BOLD}
      >
        {t('translation:candidate_creation_form.title_working_experience')}
      </Typography.Title2>
      <ExperienceList
        companiesOptions={companiesOptions}
        positionsOptions={positionsOptions}
        maxCount={10}
        form={form}
      />
      <Popup open={openLoaderInfoCandidate} className="form-candidate-creation__extracting-popup">
        <Loader />
        <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
          {t('translation:candidate_creation_form.text_please_wait')}
        </Typography.Title3>
        <Typography.Text>{t('translation:candidate_creation_form.text_extracting_information')}</Typography.Text>
      </Popup>
      <Popup
        open={!!importUrl}
        title={t('translation:candidate_creation_form.title_content_overwritten')}
        cancelButtonProps={{
          children: t('translation:candidate_creation_form.button_back_to_page'),
          onClick: () => setImportUrl(''),
        }}
        okButtonProps={{
          children: t('translation:candidate_creation_form.button_proceed_import'),
          onClick: onImportUrl,
        }}
        className="form-candidate-creation__overwrite-popup"
      >
        <Typography.Text>{t('translation:candidate_creation_form.text_overwritten_info')}</Typography.Text>
        <Typography.Text> </Typography.Text>
        <Typography.Text>{t('translation:candidate_creation_form.text_proceed_import')}</Typography.Text>
      </Popup>
    </>
  );
};
